/**
 * Analytics module
 * Custom event/page-view tracking. Vercel Web Analytics is handled by the
 * <Analytics /> component in index.js; this module records page views into our
 * own database (first-party, so ad/privacy blockers don't drop them).
 */

import api from './api';
import { getVisitorId, getSessionId } from './visitorId';

// Track page views
const trackPageView = (pathname) => {
  try {
    if (typeof window !== 'undefined') {
      console.log(`📊 Page view: ${pathname}`);
      
      // If Google Analytics is available
      if (window.gtag) {
        window.gtag('config', 'G-PLACEHOLDER', {
          page_path: pathname
        });
      }
      
      // Record the page view in our own database (first-party, blocker-proof).
      // `api` attaches the auth token when present, so logged-in views are
      // attributed to the user; anonymous views fall back to a visitor id.
      api.post('/api/analytics/pageview', {
        path: pathname,
        referrer: document.referrer || '',
        visitorId: getVisitorId(),
        sessionId: getSessionId()
      }).catch(() => {
        // Ignore errors - analytics is best-effort and must never break the app.
      });
    }
  } catch (err) {
    // Silently fail for analytics errors
    console.log('Analytics error:', err.message);
  }
};

// Track events (such as button clicks)
const trackEvent = (category, action, label = '', value = null) => {
  try {
    if (typeof window !== 'undefined') {
      console.log(`📊 Event: ${category} / ${action} / ${label} / ${value}`);
      
      // Vercel Analytics - track events
      try {
        if (window?.va) {
          window.va('event', {
            name: action,
            data: {
              category,
              label,
              value
            }
          });
        }
      } catch (vercelErr) {
        console.log('Vercel Analytics event error:', vercelErr.message);
      }
      
      // If Google Analytics is available
      if (window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value
        });
      }
      
      // Send to server if configured
      if (process.env.REACT_APP_ANALYTICS_ENDPOINT) {
        const data = {
          type: 'event',
          category,
          action,
          label,
          value,
          timestamp: new Date().toISOString()
        };
        
        fetch(process.env.REACT_APP_ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          keepalive: true // Send even when page unloads
        }).catch(() => {
          // Ignore errors - this is just analytics
        });
      }
    }
  } catch (err) {
    // Silently fail for analytics errors
    console.log('Analytics error:', err.message);
  }
};

const trackLocalMatch = (game) => {
  api.post('/api/analytics/local-match', { game, visitorId: getVisitorId() }).catch(() => {
    // Best-effort, like page views.
  });
};

export default {
  trackPageView,
  trackEvent,
  trackLocalMatch
};